import React, { useState } from "react";
import {
  Box,
  Typography,
  Divider,
  Collapse,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import { ConvertDDToDM } from "@/utils/geoFormat";
import RouteHelper from "@/utils/routeHelper";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
dayjs.extend(utc);

const LABEL_SX = { fontSize: 11, color: "#6b7280" };
const VALUE_SX = { fontSize: 11, color: "#004f7d", fontWeight: 600 };
const ROW_SX = { display: "flex", justifyContent: "space-between", mb: 0.35 };

function Row({ label, value }) {
  return (
    <Box sx={ROW_SX}>
      <Typography sx={LABEL_SX}>{label}</Typography>
      <Typography sx={VALUE_SX}>{value ?? "N/A"}</Typography>
    </Box>
  );
}

function formatValue(value, fallback = "N/A") {
  if (value === null || value === undefined || value === "") return fallback;
  return value;
}

function formatDegrees(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return "N/A";
  const normalized = ((n % 360) + 360) % 360;
  return `${Math.round(normalized)}°`;
}

export default function VesselCardContent({ selectedVessel }) {
  const [detailsExpanded, setDetailsExpanded] = useState(false);

  const feature = selectedVessel?.geojson?.features?.[0] ?? {};
  const latestFeature =
    selectedVessel?.geojson?.features?.[selectedVessel?.geojson?.features?.length - 1] ?? null;
  const props = feature?.properties ?? {};
  const report = props?.albis_reports ?? {};
  const sensor = props?.albis_sensors ?? {};
  const analyse = props?.analyse ?? {};

  const isReported = selectedVessel?.routeType === "reported";
  const sog = props?.sog ?? sensor?.speedOverGround_kn;
  // Planned vessels have no heading sensor; a last-position marker has no course.
  const showHeading = !RouteHelper.isPlanType(selectedVessel?.routeType);
  const heading = props?.heading ?? sensor?.headingTrue_deg;
  const cog = isReported ? analyse?.course_bearing : analyse?.rhumbline_bearing;
  const showCog = !isReported || analyse?.course_bearing != null;
  const etaRaw = report?.plannedETA_local_ISO;
  const eta = etaRaw ? dayjs.utc(etaRaw).format("DD MMM YYYY HH:mm [UTC]") : "N/A";
  const nextPort = report?.nextPort_LOCODE;
  const latestTsRaw =
    latestFeature?.properties?.timestamp ??
    latestFeature?.properties?.measuredAt ??
    null;
  const latestTs = latestTsRaw
    ? dayjs.utc(latestTsRaw).format("DD MMM YYYY HH:mm [UTC]")
    : "N/A";

  // Current vessel position — same Time / Position header as the waypoint card
  // so the two cards feel like one family.
  const positionTsRaw =
    feature?.properties?.timestamp ??
    feature?.properties?.measuredAt ??
    latestTsRaw;
  const positionTs = positionTsRaw
    ? dayjs.utc(positionTsRaw).format("DD MMM YYYY HH:mm [UTC]")
    : "N/A";
  const coords = feature?.geometry?.coordinates;
  const positionStr = coords ? ConvertDDToDM(coords).join("   ") : "N/A";

  return (
    <>
      <Box sx={{ px: 1, py: 0.75 }}>
        <Row label="Time" value={positionTs} />
        <Box sx={ROW_SX}>
          <Typography sx={LABEL_SX}>Position</Typography>
          <Typography sx={{ ...VALUE_SX, fontSize: 10 }}>{positionStr}</Typography>
        </Box>
        <Divider sx={{ my: 0.5 }} />
        <Row label="SOG" value={Number.isFinite(Number(sog)) ? `${Number(sog).toFixed(1)} kn` : "N/A"} />
        {showHeading && <Row label="Heading" value={formatDegrees(heading)} />}
        {showCog && <Row label="COG" value={formatDegrees(cog)} />}
        <Divider sx={{ my: 0.5 }} />
        <Row label="Next Port" value={formatValue(nextPort)} />
        <Row label="ETA" value={eta} />
        <Row label="Last Update" value={latestTs} />
      </Box>

      <Box
        onClick={() => setDetailsExpanded((prev) => !prev)}
        sx={{
          px: 1,
          py: 0.5,
          borderTop: "1px solid rgba(0,79,125,0.12)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: 0.5,
          color: "#004f7d",
          cursor: "pointer",
          pointerEvents: "auto",
          userSelect: "none",
          "&:hover": { backgroundColor: "rgba(0,79,125,0.06)" },
        }}
      >
        <Typography sx={{ fontSize: 11, fontWeight: 600 }}>
          {detailsExpanded ? "Hide details" : "Show details"}
        </Typography>
        {detailsExpanded ? (
          <ExpandLessIcon sx={{ fontSize: 16 }} />
        ) : (
          <ExpandMoreIcon sx={{ fontSize: 16 }} />
        )}
      </Box>
      <Collapse in={detailsExpanded}>
        <Divider />
        <Box sx={{ px: 1, py: 0.75 }}>
          <Row
            label="Center Draft"
            value={
              report?.draftAft_m && report?.draftForward_m
                ? `${((Number(report.draftAft_m) + Number(report.draftForward_m)) / 2).toFixed(1)} m`
                : "N/A"
            }
          />
          <Row
            label="M/E FCR"
            value={
              Number.isFinite(Number(sensor?.mainEngineFuelConsumptionRate_t_per_d))
                ? `${Number(sensor.mainEngineFuelConsumptionRate_t_per_d).toFixed(1)} t/d`
                : "N/A"
            }
          />
          <Row
            label="A/E FCR"
            value={
              Number.isFinite(Number(sensor?.auxEngineFuelConsumptionRate_t_per_d))
                ? `${Number(sensor.auxEngineFuelConsumptionRate_t_per_d).toFixed(1)} t/d`
                : "N/A"
            }
          />
          <Row label="Sailing Type" value={formatValue(report?.typeOfSailing)} />
        </Box>
      </Collapse>
    </>
  );
}
